import React, { useState, useEffect, useRef, useMemo } from "react";
import styled, { keyframes } from "styled-components";
import logoImage from "../assets/Frame 1000005895.png";
import upgradeButtonImage from "../assets/Frame 9.png";
import connectButtonImage from "../assets/Frame 5 (3).png";
import downloadButtonImage from "../assets/Frame 7.png";
import publishButtonImage from "../assets/Frame 8.png";
import previewIconImage from "../assets/image- (2) 1.png";
import tortoiseImage from "../assets/tortoise.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHistory,
  faEraser,
  faExpand,
  faRedo,
  faCode,
  faImage,
} from "@fortawesome/free-solid-svg-icons";
import { chatAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import mobileLogoImage from "../assets/Frame 1000005895.png";
import mobileProjectImage from "../assets/Frame 1000005951 (1).png";
import mobileConnectImage from "../assets/Frame 1000005951 (1).png";
import mobilePublishImage from "../assets/Frame 1000005953 (1).png";
import ProfileMenu from "../components/ProfileMenu";
import LoginModal from "../components/LoginModal.js";
import ShareModal from "../components/ShareModal";
import * as gameService from "../services/gameService";

const blinkAnimation = keyframes`
  0% { opacity: 0.2; }
  50% { opacity: 1; }
  100% { opacity: 0.2; }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 0;
`;

const TypingDot = styled.span`
  width: 8px;
  height: 8px;
  background-color: #999;
  border-radius: 50%;
  animation: typingAnimation 1.4s infinite;
  animation-delay: ${(props) => props.delay || "0s"};

  @keyframes typingAnimation {
    0% {
      opacity: 0.2;
      transform: scale(0.8);
    }
    50% {
      opacity: 1;
      transform: scale(1);
    }
    100% {
      opacity: 0.2;
      transform: scale(0.8);
    }
  }
`;

const MessageContent = styled.div`
  white-space: pre-wrap;
  line-height: 1.5;
  max-width: 100%;
  overflow-wrap: break-word;
  transition: opacity 0.2s ease;
  animation: ${fadeIn} 0.3s ease;
  font-family: "Space Grotesk", sans-serif;
`;

const CodeBlock = styled.pre`
  background-color: #0d1116;
  border-radius: 6px;
  color: #e6edf3;
  font-family: "Space Grotesk", "Consolas", "Monaco", monospace;
  font-size: 14px;
  line-height: 1.45;
  overflow: auto;
  padding: 16px;
  position: relative;
  margin: 16px 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  border: 1px solid #30363d;
`;

const CodeLanguageHeader = styled.div`
  background-color: #2d2d2d;
  color: #e6e6e6;
  padding: 6px 12px;
  border-radius: 8px 8px 0 0;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: "Space Grotesk", sans-serif;
`;

const CodeBlockWrapper = styled.div`
  margin: 12px 0;
  width: 100%;
`;

const dropIn = keyframes`
  0% {
    transform: translateY(-100px) scale(0.7);
    opacity: 0;
  }
  70% {
    transform: translateY(10px) scale(1.05);
    opacity: 1;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

const bounce = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
`;

const ChatPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState("");
  const [activeSession, setActiveSession] = useState(null);
  const [sessionTitle, setSessionTitle] = useState("New Project");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isSavingTitle, setIsSavingTitle] = useState(false);
  const [previewCode, setPreviewCode] = useState("");
  const [activeTab, setActiveTab] = useState("preview");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showHistory, setShowHistory] = useState(false);
  const [userSessions, setUserSessions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isRemixMode, setIsRemixMode] = useState(false);
  const [remixSessionId, setRemixSessionId] = useState(null);

  // Refs for DOM operations
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const initialSearchQueryRef = useRef(null);
  const titleInputRef = useRef(null);
  const [streamingStatus, setStreamingStatus] = useState("idle");
  const [streamProgress, setStreamProgress] = useState(0);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareableLink, setShareableLink] = useState("");
  const [publishError, setPublishError] = useState(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Initialize URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    // Check for search query
    const searchQuery = searchParams.get("query");
    if (searchQuery) {
      initialSearchQueryRef.current = searchQuery;
    }

    // Check for remix mode
    if (searchParams.get("mode") === "remix") {
      setIsRemixMode(true);
      const remixId = searchParams.get("remixSessionId");
      if (remixId) {
        setRemixSessionId(remixId);
      }

      // Check for original game ID
      const originalId = searchParams.get("originalGameId");
      if (originalId) {
        console.log("Original game ID:", originalId);
      }
    }

    // Check for session ID in URL
    const sessionIdParam = searchParams.get("sessionId");
    if (sessionIdParam) {
      console.log("Loading session from URL parameter:", sessionIdParam);
      setSessionId(sessionIdParam);
      // We'll load the chat session after the component is fully initialized
      setTimeout(() => {
        loadChatSession(sessionIdParam);
      }, 0);
    }
  }, [location.search]);

  // Initialize the search query from URL
  const initialSearchQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("query") || "";
  }, [location.search]);

  // Get user display name for the UI
  const getUserDisplayName = () => {
    if (currentUser?.firstName && currentUser?.lastName) {
      return `${currentUser.firstName} ${currentUser.lastName}`;
    } else if (currentUser?.firstName) {
      return currentUser.firstName;
    } else if (currentUser?.email) {
      // Show full email
      return currentUser.email;
    }
    return "User";
  };

  // Get user ID - critical for identifying user-specific chat sessions
  const getUserId = () => {
    if (currentUser?.id) return currentUser.id;

    const storedUser = localStorage.getItem("currentUser");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        return parsedUser.id || parsedUser.user_id;
      } catch (e) {
        console.error("Error parsing stored user:", e);
      }
    }

    // If no user ID found, generate a temporary one for guest access
    let guestId = localStorage.getItem("guestUserId");
    if (!guestId) {
      guestId = `guest-${Date.now()}-${Math.random()
        .toString(36)
        .substring(2, 9)}`;
      localStorage.setItem("guestUserId", guestId);
    }
    return guestId;
  };

  // Load all chat sessions for the current user
  const loadUserSessions = async () => {
    const userId = getUserId();
    if (!userId) {
      setError("You must be logged in to view your chat history");
      return;
    }

    setLoadingHistory(true);
    try {
      // Call the API to get all sessions for this user
      const sessions = await chatAPI.getSessions(userId);

      // Normalize the session data to ensure consistent format
      const normalizedSessions = sessions.map((session) => ({
        ...session,
        // Ensure both id and session_id properties exist
        id: session.id || session.session_id,
        session_id: session.session_id || session.id,
        // Ensure title exists
        title: session.title || "Untitled Project",
      }));

      setUserSessions(normalizedSessions || []);
      console.log(
        `Loaded ${normalizedSessions?.length || 0} sessions for user ${userId}`
      );
    } catch (err) {
      console.error("Failed to load sessions:", err);
      setError("Failed to load your chat history. Please try again.");
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load messages for a specific chat session
  const loadChatSession = async (sessionId) => {
    if (!sessionId) return;

    const userId = getUserId();
    if (!userId) {
      setError("You must be logged in to load chat history");
      return;
    }

    setLoading(true);
    try {
      console.log(`Loading chat session: ${sessionId}`);

      // If userSessions is empty, load them first
      if (userSessions.length === 0) {
        await loadUserSessions();
      }

      // Find the session in userSessions - check both id and session_id properties
      let session = userSessions.find(
        (s) => s.id === sessionId || s.session_id === sessionId
      );

      if (session) {
        console.log(`Found session: ${session.title || "Untitled Project"}`);
        // Normalize the session object to ensure it has an id property
        const normalizedSession = {
          ...session,
          id: session.id || session.session_id, // Ensure id property exists
        };
        setActiveSession(normalizedSession);
        // Set the session title
        setSessionTitle(session.title || "Untitled Project");
      } else {
        console.log("Session not found in current sessions list");
        // Create a new active session object if needed
        const newSession = {
          id: sessionId,
          session_id: sessionId, // Include both formats
          title: "New Project",
          created_at: new Date().toISOString(),
          message_count: 0,
        };
        setActiveSession(newSession);
        setSessionTitle(newSession.title);
      }

      // Fetch messages for this session using the API
      const sessionMessages = await chatAPI.getSessionMessages(
        sessionId,
        userId
      );

      if (sessionMessages && sessionMessages.length > 0) {
        console.log("Received messages from API:", sessionMessages);

        // Format the messages from the API into our expected format
        // Handle multiple potential message formats
        const formattedMessages = sessionMessages.map((msg) => {
          // Check if the message has a 'type' property (new format)
          if (msg.type) {
            return {
              id: msg.id || Date.now() + Math.random(),
              sender: msg.type === "HumanMessage" ? "user" : "ai",
              text: msg.content || "",
            };
          }
          // Check if the message has a 'role' property (older format)
          else if (msg.role) {
            return {
              id: msg.id || Date.now() + Math.random(),
              sender: msg.role === "user" ? "user" : "ai",
              text: msg.content || msg.text || "",
            };
          }
          // Default case if message format is not recognized
          else {
            // Try to determine the sender based on available properties
            const isUserMessage =
              msg.isUser || msg.is_user || msg.sender === "user";
            return {
              id: msg.id || Date.now() + Math.random(),
              sender: isUserMessage ? "user" : "ai",
              text: msg.content || msg.text || msg.message || "",
            };
          }
        });

        setMessages(formattedMessages);
        setSessionId(sessionId);
        console.log(
          `Loaded session ${sessionId} with ${formattedMessages.length} messages`
        );

        // Update the extracted code for preview if any AI messages
        updateExtractedCode(
          formattedMessages.filter((msg) => msg.sender === "ai")
        );

        // Check if we have previewCode in localStorage to restore it
        try {
          const key = `chat_${userId}_${sessionId}`;
          const localSessionData = localStorage.getItem(key);
          if (localSessionData) {
            const parsedData = JSON.parse(localSessionData);
            if (parsedData.previewCode && parsedData.previewCode.length > 0) {
              console.log("Restored previewCode from localStorage");
              setPreviewCode(parsedData.previewCode);
              setActiveTab("preview");
            }
          }
        } catch (e) {
          console.error("Error checking localStorage for previewCode:", e);
        }
      } else {
        // If no messages returned, initialize with a welcome message
        console.log(
          "No messages found for session, initializing with welcome message"
        );
        setSessionId(sessionId);

        const welcomeMessageId = Date.now();
        setMessages([
          {
            id: welcomeMessageId,
            sender: "ai",
            text: "",
            isLoading: true,
          },
        ]);

        // Animate the welcome message
        const welcomeMessage = `Hello ${getUserDisplayName()}! I can help you build your project. What would you like to create?`;
        animateInitialMessage(welcomeMessage, setMessages, welcomeMessageId);
      }
    } catch (err) {
      console.error("Failed to load chat session:", err);
      setError("Failed to load chat history");

      // Fallback to a welcome message
      setSessionId(sessionId);

      const welcomeMessageId = Date.now();
      setMessages([
        {
          id: welcomeMessageId,
          sender: "ai",
          text: "",
          isLoading: true,
        },
      ]);

      // Animate the welcome message
      const welcomeMessage = `Hello ${getUserDisplayName()}! I can help you build your project. What would you like to create?`;
      animateInitialMessage(welcomeMessage, setMessages, welcomeMessageId);
    } finally {
      setLoading(false);
    }
  };

  // Toggle history visibility
  const toggleHistory = async () => {
    // If we're showing history for the first time, load the sessions
    if (!showHistory && userSessions.length === 0) {
      await loadUserSessions();
    }
    setShowHistory(!showHistory);
  };

  // Load user sessions when component mounts or user changes
  useEffect(() => {
    // Skip the standard initialization if in remix mode
    if (isRemixMode) return;

    const userId = getUserId();
    if (userId) {
      console.log(`User logged in: ${userId}`);

      // Initialize with welcome message, but don't load sessions automatically
      const welcomeMessageId = Date.now();
      setMessages([
        {
          id: welcomeMessageId,
          sender: "ai",
          text: "", // Start with empty text
          isLoading: true, // Show loading initially
        },
      ]);

      // Animate the welcome message
      const welcomeMessage = `Welcome back, ${getUserDisplayName()}! How can I help you with your project today?`;
      animateInitialMessage(welcomeMessage, setMessages, welcomeMessageId);

      // Try to load the last active session if available
      const lastSessionId = localStorage.getItem(`currentSessionId_${userId}`);
      if (lastSessionId) {
        setSessionId(lastSessionId);
      }
    }
  }, [currentUser, isRemixMode]); // Re-run when user or remix mode changes

  // Save the current chat session
  const saveChatSession = () => {
    if (!sessionId) return;

    const userId = getUserId();
    if (!userId) return;

    const key = `chat_${userId}_${sessionId}`;
    const sessionData = {
      id: sessionId,
      userId,
      messages,
      title: sessionTitle || "Untitled Project",
      isRemix: isRemixMode,
      previewCode: previewCode || "",
      updatedAt: new Date().toISOString(),
      createdAt: localStorage.getItem(key)
        ? JSON.parse(localStorage.getItem(key)).createdAt
        : new Date().toISOString(),
    };

    localStorage.setItem(key, JSON.stringify(sessionData));
    console.log(`Saved session ${sessionId} with ${messages.length} messages`);
  };

  // Save chat messages to localStorage when they change
  useEffect(() => {
    if (sessionId && messages.length > 0) {
      saveChatSession();
    }
  }, [sessionId, messages]);

  // Scroll to the bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Save session ID to localStorage when it changes
  useEffect(() => {
    const userId = getUserId();
    if (userId && sessionId) {
      localStorage.setItem(`currentSessionId_${userId}`, sessionId);

      // If we have a session ID but no active session data, try to find it in the userSessions
      if (sessionId && !activeSession && userSessions.length > 0) {
        // Check for both id and session_id formats
        const session = userSessions.find(
          (s) => s.id === sessionId || s.session_id === sessionId
        );
        if (session) {
          // Normalize the session object
          const normalizedSession = {
            ...session,
            id: session.id || session.session_id,
          };
          setActiveSession(normalizedSession);
          setSessionTitle(session.title || "Untitled Project");
          console.log(
            `Set active session from sessions list: ${
              session.title || "Untitled Project"
            }`
          );
        } else {
          // If we can't find the session in userSessions, load it
          loadChatSession(sessionId);
        }
      }
    }
  }, [sessionId, activeSession, userSessions]);

  // Helper function to extract HTML code from AI messages
  const updateExtractedCode = (aiMessages) => {
    if (!aiMessages || aiMessages.length === 0) return;

    let latestHtmlCode = "";

    // Loop through AI messages in reverse to find the latest HTML code
    for (let i = aiMessages.length - 1; i >= 0; i--) {
      const text = aiMessages[i].text;
      // Try to match HTML code blocks
      const htmlMatch = text.match(/```html\s*([\s\S]*?)\s*```/);

      if (htmlMatch && htmlMatch[1]) {
        latestHtmlCode = htmlMatch[1].trim();
        break;
      }
    }

    if (latestHtmlCode) {
      console.log("Found HTML code in messages, updating preview");
      setPreviewCode(latestHtmlCode);
      // Set the active tab to preview if we found HTML
      setActiveTab("preview");
    }
  };

  // Extract HTML code from AI messages for preview
  useEffect(() => {
    if (messages.length > 0) {
      // Get only AI messages
      const aiMessages = messages.filter((msg) => msg.sender === "ai");
      updateExtractedCode(aiMessages);
    }
  }, [messages]);

  // Extract code blocks from messages for preview tab
  const extractCodeForPreview = (messages) => {
    if (!messages || messages.length === 0) return "";

    // Focus on AI messages as they contain the code
    const aiMessages = messages.filter((msg) => msg.sender === "ai");

    // Look for HTML code blocks in the most recent messages first
    for (let i = aiMessages.length - 1; i >= 0; i--) {
      const message = aiMessages[i];
      const htmlMatch = message.text.match(/```html\s*([\s\S]*?)\s*```/);

      if (htmlMatch && htmlMatch[1]) {
        return htmlMatch[1].trim();
      }
    }

    // If no HTML code blocks found, look for any code blocks
    for (let i = aiMessages.length - 1; i >= 0; i--) {
      const message = aiMessages[i];
      const codeMatch = message.text.match(
        /```(?:html|javascript|css)?\s*([\s\S]*?)\s*```/
      );

      if (codeMatch && codeMatch[1]) {
        return codeMatch[1].trim();
      }
    }

    return "";
  };

  // Handle search query from URL to auto-send message
  useEffect(() => {
    const searchQuery = initialSearchQueryRef.current;
    if (searchQuery && getUserId()) {
      // We need to set a slight delay to ensure all initializations are complete
      const timer = setTimeout(() => {
        // Set message to search query
        setMessage(searchQuery);

        // Create a new chat if needed (this is important for search queries to start fresh)
        handleNewChat(false);

        // Then submit it after a short delay to ensure state is updated
        const submitTimer = setTimeout(() => {
          const submitEvent = { preventDefault: () => {} };
          handleSendMessage(submitEvent, searchQuery);

          // Clear query parameter from URL (to prevent re-sending on refresh)
          navigate("/chat", { replace: true });
        }, 300);

        return () => clearTimeout(submitTimer);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [initialSearchQueryRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle new chat button click
  const handleNewChat = (clearUrl = true) => {
    setSessionId("");
    setActiveSession(null);
    setSessionTitle("New Project");

    // Generate an ID for the welcome message
    const welcomeMessageId = Date.now();
    setMessages([
      {
        id: welcomeMessageId,
        sender: "ai",
        text: "", // Start with empty text
        isLoading: true,
      },
    ]);

    // Animate the welcome message
    const welcomeMessage = `Hello ${getUserDisplayName()}! I can help you build your project. What would you like to create?`;
    animateInitialMessage(welcomeMessage, setMessages, welcomeMessageId);

    // Clear search query from URL if needed
    if (clearUrl && location.search) {
      navigate("/chat", { replace: true });
    }
  };

  // Function to update message UI - defined at component level to be accessible everywhere
  const updateMessageUI = (msgId, text) => {
    setMessages((prev) => {
      // Don't update if the text is the same (avoid unnecessary renders)
      const existingMsg = prev.find((m) => m.id === msgId);
      if (existingMsg && existingMsg.text === text) {
        return prev;
      }

      // Create updated messages array
      const updated = prev.map((msg) =>
        msg.id === msgId
          ? {
              id: msgId,
              sender: "ai",
              text: text,
              isLoading: false,
            }
          : msg
      );
      return updated;
    });

    // Force scroll to bottom
    setTimeout(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
      }
    }, 50);
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const handleSendMessage = async (e, overrideMessage = null) => {
    if (e) e.preventDefault();

    // Get the message either from override or from state
    const messageText = overrideMessage || message;

    // Don't send empty messages
    if (!messageText.trim()) return;

    // Clear the input field
    setMessage("");

    // Create a user message ID
    const userMessageId = Date.now();

    // Add the user message to the UI
    setMessages([
      ...messages,
      { id: userMessageId, sender: "user", text: messageText },
    ]);

    // Create a loading message ID for the AI response
    const loadingMessageId = Date.now() + 1;

    // Add loading message
    setMessages((prevMessages) => [
      ...prevMessages,
      { id: loadingMessageId, sender: "ai", text: "", isLoading: true },
    ]);

    // Scroll to bottom
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);

    try {
      // Get the current user ID
      const userId = getUserId();

      // Check or create a session ID
      let currentSessionId = sessionId;

      if (!currentSessionId) {
        // Generate a session ID if needed
        currentSessionId = `session-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`;
        setSessionId(currentSessionId);

        // Save in localStorage
        localStorage.setItem(`currentSessionId_${userId}`, currentSessionId);
      }

      // Check if we're in remix mode
      if (isRemixMode) {
        try {
          // In remix mode, we want to process the HTML updates
          const loadingMsgId = loadingMessageId;

          // Try to use the standard API endpoint instead of the missing remix-start endpoint
          const response = await fetch(
            `https://api.lab.battlexo.com/api/v1/groq/chat/stream`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                session_id: currentSessionId,
                user_id: userId,
                message: messageText,
                html: previewCode, // Include the current HTML
                project_name: "Remix Project",
              }),
            }
          );

          if (!response.ok) {
            // If the endpoint fails, fall back to client-side handling
            console.warn(
              `API returned status ${response.status}, falling back to client-side handling`
            );
            throw new Error(`API error: ${response.status}`);
          }

          // Check the content type to determine how to process the response
          const contentType = response.headers.get("content-type");
          let responseData;

          if (contentType && contentType.includes("application/json")) {
            // Handle JSON response
            responseData = await response.json();
          } else {
            // Handle text/plain or other response types
            const textResponse = await response.text();

            // Try to extract HTML from text response
            const htmlMatch = textResponse.match(/```html\s*([\s\S]*?)\s*```/);

            responseData = {
              response: textResponse,
              html: htmlMatch ? htmlMatch[1].trim() : null,
            };
          }

          // Update the preview with the new HTML if available
          if (responseData.html) {
            setPreviewCode(responseData.html);
          } else if (responseData.response) {
            // Try to extract HTML from the response
            const htmlMatch = responseData.response.match(
              /```html\s*([\s\S]*?)\s*```/
            );
            if (htmlMatch && htmlMatch[1]) {
              setPreviewCode(htmlMatch[1].trim());
            }
          }

          // Update the AI response message
          updateMessageUI(loadingMsgId, responseData.response);
        } catch (error) {
          console.warn("Remix mode API error:", error);
          // Display a specific error message for remix mode instead of falling back

          // Add a helpful message explaining the issue to the user
          updateMessageUI(
            loadingMessageId,
            `I'm having trouble processing your request in remix mode. The Game Simulator is still loading. You can continue chatting with me or try refreshing the page if the game doesn't appear in the preview.`
          );

          // Update the preview code with a simplified version if we don't have any yet
          if (!previewCode || previewCode.trim() === "") {
            const simplifiedTemplate = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Flight Simulator</title>
  <style>
    body { 
      margin: 0; padding: 0; 
      background: linear-gradient(to bottom, #0f2027, #203a43, #2c5364);
      font-family: Arial, sans-serif;
      color: white;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      overflow: hidden;
    }
    .message {
      text-align: center;
      padding: 2rem;
      background: rgba(0,0,0,0.5);
      border-radius: 10px;
      max-width: 80%;
    }
  </style>
</head>
<body>
  <div class="message">
    <h1>Flight Simulator</h1>
    <p>The game is loading... Please wait a moment.</p>
    <p>If the game doesn't appear, please try asking a question about building a flight simulator.</p>
  </div>
</body>
</html>`;
            setPreviewCode(simplifiedTemplate);
          }
        }
      } else {
        // Normal chat mode - process the streaming response
        await processStreamingResponse(
          currentSessionId,
          userId,
          messageText,
          loadingMessageId
        );
      }
    } catch (error) {
      console.error("Error sending message:", error);

      // Update the loading message with the error
      setMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === loadingMessageId
            ? {
                ...msg,
                text: "Sorry, there was an error processing your request. Please try again.",
                isLoading: false,
                isError: true,
              }
            : msg
        )
      );
    }
  };

  // Improved function to handle streaming API responses
  const processStreamingResponse = async (
    sessionId,
    userId,
    message,
    loadingMsgId
  ) => {
    try {
      console.log(`Starting stream with session: ${sessionId}`);
      setStreamingStatus("connecting");
      setStreamProgress(0);

      const response = await fetch(
        "https://api.lab.battlexo.com/api/v1/groq/chat/stream",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            session_id: sessionId,
            user_id: userId,
            message: message,
            project_name: "My Project",
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          `Streaming request failed with status ${response.status}`
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let fullText = "";

      setStreamingStatus("streaming");

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        fullText += chunk;

        updateMessageUI(loadingMsgId, fullText);
      }

      // Final update
      updateMessageUI(loadingMsgId, fullText);

      // Post-process message
      updateExtractedCode([
        ...messages,
        {
          id: loadingMsgId,
          text: fullText,
          sender: "ai",
          isLoading: false,
        },
      ]);

      setStreamingStatus("complete");
      return true;
    } catch (err) {
      console.error("Streaming error:", err);
      setStreamingStatus("error");

      updateMessageUI(
        loadingMsgId,
        "Sorry, something went wrong. Try again later."
      );
      return false;
    } finally {
      setTimeout(() => {
        setStreamingStatus("idle");
        setStreamProgress(0);
      }, 500);
    }
  };

  const handleClearMemory = async () => {
    const userId = getUserId();
    if (!userId) {
      setError("You must be logged in to clear chat history");
      return;
    }

    setLoading(true);
    try {
      // Clear current session if one exists
      if (sessionId) {
        try {
          // Try to delete the session from the server
          await chatAPI.deleteSession(sessionId, userId);
          console.log(`Deleted session ${sessionId} from server`);
        } catch (err) {
          console.warn(
            "Failed to delete session from server, falling back to local delete:",
            err
          );

          // Remove from localStorage
          const key = `chat_${userId}_${sessionId}`;
          localStorage.removeItem(key);
        }
      }

      // Clear current session ID for this user
      localStorage.removeItem(`currentSessionId_${userId}`);

      // Reset UI state
      setSessionId("");
      const welcomeMessageId = Date.now();
      setMessages([
        {
          id: welcomeMessageId,
          sender: "ai",
          text: "",
          isLoading: true,
        },
      ]);

      // Animate the welcome message
      const welcomeMessage = `Hello ${getUserDisplayName()}! I can help you build your project. What would you like to create?`;
      animateInitialMessage(welcomeMessage, setMessages, welcomeMessageId);

      // Refresh the sessions list
      await loadUserSessions();

      console.log("Chat memory cleared for user", userId);
    } catch (err) {
      console.error("Error clearing chat memory:", err);
      setError("Failed to clear chat history");
    } finally {
      setLoading(false);
    }
  };

  // Update the preview code when messages change
  useEffect(() => {
    const code = extractCodeForPreview(messages);
    setPreviewCode(code);
  }, [messages]);

  // Function to render the assets tab content
  const renderAssetsTab = () => {
    return (
      <AssetsContainer>
        <TortoiseImageContainer>
          <TortoiseImage src={tortoiseImage} alt="Cute Tortoise" />
          <ComingSoonText>Dropping soon !</ComingSoonText>
        </TortoiseImageContainer>
      </AssetsContainer>
    );
  };

  // Render correct content based on active tab
  const renderTabContent = () => {
    if (isRemixMode) {
      return (
        <PreviewSection>
          <TabsContainer>
            <Tab
              active={activeTab === "preview"}
              onClick={() => setActiveTab("preview")}
            >
              <TabIcon active={activeTab === "preview"}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 5C17.5228 5 22 8 22 12C22 16 17.5228 19 12 19C6.47715 19 2 16 2 12C2 8 6.47715 5 12 5Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </TabIcon>
              Preview
            </Tab>
            <Tab
              active={activeTab === "assets"}
              onClick={() => setActiveTab("assets")}
            >
              <TabIcon active={activeTab === "assets"}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 16V8.00002C20.9996 7.6493 20.9071 7.30483 20.7315 7.00119C20.556 6.69754 20.3037 6.44539 20 6.27002L13 2.27002C12.696 2.09449 12.3511 2.00208 12 2.00208C11.6489 2.00208 11.304 2.09449 11 2.27002L4 6.27002C3.69626 6.44539 3.44398 6.69754 3.26846 7.00119C3.09294 7.30483 3.00036 7.6493 3 8.00002V16C3.00036 16.3508 3.09294 16.6952 3.26846 16.9989C3.44398 17.3025 3.69626 17.5547 4 17.73L11 21.73C11.304 21.9056 11.6489 21.998 12 21.998C12.3511 21.998 12.696 21.9056 13 21.73L20 17.73C20.3037 17.5547 20.556 17.3025 20.7315 16.9989C20.9071 16.6952 20.9996 16.3508 21 16Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </TabIcon>
              Assets
            </Tab>
            <Tab
              active={activeTab === "code"}
              onClick={() => setActiveTab("code")}
            >
              <TabIcon active={activeTab === "code"}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16 18L22 12L16 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M8 6L2 12L8 18"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </TabIcon>
              Code
            </Tab>
          </TabsContainer>

          <TabContent>
            {activeTab === "preview" && (
              <PreviewContainer>
                <PreviewControls>
                  <RefreshButton>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 3v5h5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M16 21h5v-5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </RefreshButton>
                  <ExpandButton>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 3h6v6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M9 21H3v-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M21 3l-7 7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M3 21l7-7"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ExpandButton>
                </PreviewControls>
                {previewCode ? (
                  <iframe
                    srcDoc={previewCode}
                    title="Preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      border: "none",
                      borderRadius: "0",
                      backgroundColor: "white",
                    }}
                  />
                ) : (
                  <EmptyPreviewContent>
                    <GradientBackground />
                  </EmptyPreviewContent>
                )}
              </PreviewContainer>
            )}

            {activeTab === "assets" && renderAssetsTab()}

            {activeTab === "code" && (
              <CodeDisplay>
                <pre>
                  <code>{previewCode}</code>
                </pre>
              </CodeDisplay>
            )}
          </TabContent>
        </PreviewSection>
      );
    }

    // Original rendering logic for standard chat mode
    return (
      <PreviewSection>
        <TabsContainer>
          <Tab
            active={activeTab === "preview"}
            onClick={() => setActiveTab("preview")}
          >
            <TabIcon active={activeTab === "preview"}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 5C17.5228 5 22 8 22 12C22 16 17.5228 19 12 19C6.47715 19 2 16 2 12C2 8 6.47715 5 12 5Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </TabIcon>
            Preview
          </Tab>
          <Tab
            active={activeTab === "assets"}
            onClick={() => setActiveTab("assets")}
          >
            <TabIcon active={activeTab === "assets"}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 16V8.00002C20.9996 7.6493 20.9071 7.30483 20.7315 7.00119C20.556 6.69754 20.3037 6.44539 20 6.27002L13 2.27002C12.696 2.09449 12.3511 2.00208 12 2.00208C11.6489 2.00208 11.304 2.09449 11 2.27002L4 6.27002C3.69626 6.44539 3.44398 6.69754 3.26846 7.00119C3.09294 7.30483 3.00036 7.6493 3 8.00002V16C3.00036 16.3508 3.09294 16.6952 3.26846 16.9989C3.44398 17.3025 3.69626 17.5547 4 17.73L11 21.73C11.304 21.9056 11.6489 21.998 12 21.998C12.3511 21.998 12.696 21.9056 13 21.73L20 17.73C20.3037 17.5547 20.556 17.3025 20.7315 16.9989C20.9071 16.6952 20.9996 16.3508 21 16Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </TabIcon>
            Assets
          </Tab>
          <Tab
            active={activeTab === "code"}
            onClick={() => setActiveTab("code")}
          >
            <TabIcon active={activeTab === "code"}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16 18L22 12L16 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8 6L2 12L8 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </TabIcon>
            Code
          </Tab>
        </TabsContainer>

        <TabContent>
          {activeTab === "preview" && (
            <PreviewContainer>
              <PreviewControls>
              </PreviewControls>
              {previewCode ? (
                <iframe
                  srcDoc={previewCode}
                  title="Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    borderRadius: "0",
                    backgroundColor: "transparent",
                  }}
                />
              ) : (
                <EmptyPreviewContent>
                  <GradientBackground />
                </EmptyPreviewContent>
              )}
            </PreviewContainer>
          )}

          {activeTab === "assets" && renderAssetsTab()}

          {activeTab === "code" && (
            <>
              {previewCode ? (
                <CodeDisplay>
                  <pre>
                    <code>{previewCode}</code>
                  </pre>
                </CodeDisplay>
              ) : (
                <EmptyState>
                  <EmptyStateIcon
                    src="/empty-code.svg"
                    alt="No code available"
                  />
                  <EmptyStateText>
                    Generated code will appear here
                  </EmptyStateText>
                </EmptyState>
              )}
            </>
          )}
        </TabContent>
      </PreviewSection>
    );
  };

  // Add a function to animate initial welcome message
  const animateInitialMessage = (message, setMessages, messageId) => {
    const words = message.split(/\s+/);
    let currentText = "";
    let wordIndex = 0;

    const interval = setInterval(() => {
      if (wordIndex < words.length) {
        currentText += (wordIndex > 0 ? " " : "") + words[wordIndex];

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, text: currentText, isLoading: false }
              : msg
          )
        );

        wordIndex++;
      } else {
        clearInterval(interval);
      }
    }, 50); // 50ms delay between words

    return () => clearInterval(interval);
  };

  // Add useEffect for window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check for remix state on component mount
  useEffect(() => {
    if (isRemixMode) {
      console.log("Remix mode activated with session ID:", remixSessionId);

      // Set the session ID from the URL parameter
      setSessionId(remixSessionId || `remix-${Date.now()}`);

      // Set the session title if provided in the state
      if (location.state?.remixTitle) {
        setSessionTitle(location.state.remixTitle);

        // Update the session in userSessions if it exists
        setUserSessions((prev) =>
          prev.map((session) =>
            session.id === remixSessionId
              ? { ...session, title: location.state.remixTitle }
              : session
          )
        );
      } else {
        setSessionTitle("Remixed Project");
      }

      // Check if HTML content is in the location state
      if (location.state?.htmlContent) {
        console.log("Setting preview code from remix data");
        setPreviewCode(location.state.htmlContent);

        // Set active tab to preview
        setActiveTab("preview");

        // Add a system message indicating remix mode
        setMessages([
          {
            id: Date.now(),
            sender: "ai",
            text: "I've loaded the HTML content you're remixing. You can make changes and see the preview on the right. What would you like to modify?",
          },
        ]);

        // Save the session immediately to ensure it appears in projects
        setTimeout(() => {
          if (sessionId) {
            saveChatSession();
          }
        }, 500);
      } else {
        // No HTML content provided - set a default starter template
        const defaultTemplate = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Flight Simulator</title>
  <style>
    body { 
      margin: 0; padding: 0; 
      background: linear-gradient(to bottom, #0f2027, #203a43, #2c5364);
      font-family: Arial, sans-serif;
      color: white;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100vh;
      overflow: hidden;
    }
    .message {
      text-align: center;
      padding: 2rem;
      background: rgba(0,0,0,0.5);
      border-radius: 10px;
      max-width: 80%;
    }
  </style>
</head>
<body>
  <div class="message">
    <h1>Flight Simulator</h1>
    <p>The game is loading... Please wait a moment.</p>
    <p>If the game doesn't appear, please try asking a question about building a flight simulator.</p>
  </div>
</body>
</html>`;
        setPreviewCode(defaultTemplate);
        setActiveTab("preview");

        // Add a system message for remix mode
        setMessages([
          {
            id: Date.now(),
            sender: "ai",
            text: "Welcome to remix mode! I've loaded a basic template. You can ask me to modify or enhance it however you like.",
          },
        ]);
      }
    }
  }, [isRemixMode, remixSessionId, location.state, sessionId]);

  // Add these functions to handle the profile menu
  const toggleProfileMenu = () => {
    setIsProfileMenuOpen(!isProfileMenuOpen);
  };

  const closeProfileMenu = () => {
    setIsProfileMenuOpen(false);
  };

  // Add function to toggle login modal
  const toggleLoginModal = () => {
    setIsLoginModalOpen(!isLoginModalOpen);
  };

  const closeLoginModal = () => {
    setIsLoginModalOpen(false);
  };

  // Add a function to handle title editing
  const handleTitleEdit = () => {
    setIsEditingTitle(true);
    // Use setTimeout to ensure the input field is rendered before focusing
    setTimeout(() => {
      if (titleInputRef.current) {
        titleInputRef.current.focus();
        titleInputRef.current.select();
      }
    }, 50);
  };

  // Add a function to save the edited title
  const handleTitleSave = async () => {
    if (!sessionId) return;

    const userId = getUserId();
    if (!userId) {
      setError("You must be logged in to save the title");
      return;
    }

    setIsSavingTitle(true);
    try {
      // Call the API to update the session title
      await chatAPI.updateSessionTitle(sessionId, sessionTitle, userId);

      // Update local state
      setIsEditingTitle(false);

      // Update the session in userSessions
      setUserSessions((prev) =>
        prev.map((session) =>
          session.id === sessionId
            ? { ...session, title: sessionTitle }
            : session
        )
      );

      console.log(
        `Updated title for session ${sessionId} to "${sessionTitle}"`
      );
    } catch (err) {
      console.error("Failed to update session title:", err);
      setError("Failed to update session title");
    } finally {
      setIsSavingTitle(false);
    }
  };

  // Handle title input key press
  const handleTitleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleTitleSave();
    } else if (e.key === "Escape") {
      setIsEditingTitle(false);
      // Restore original title if the session exists
      if (activeSession) {
        setSessionTitle(activeSession.title || "Untitled Project");
      }
    }
  };

  // Add the styled component for ProfileImage
  const profileImageSrc =
    "data:image/png;base64,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";

  // Check for query parameters or state on navigation/mount
  useEffect(() => {
    // When coming from ProjectsPage, check for selected session in state
    if (location.state?.sessionId) {
      console.log(
        "Session ID found in navigation state:",
        location.state.sessionId
      );
      setSessionId(location.state.sessionId);
      loadChatSession(location.state.sessionId);
    }
    // Also check for sessionId in URL parameters (for backward compatibility)
    else {
      const searchParams = new URLSearchParams(location.search);
      const sessionIdParam = searchParams.get("sessionId");
      if (sessionIdParam) {
        console.log("Session ID found in URL parameters:", sessionIdParam);
        setSessionId(sessionIdParam);
        loadChatSession(sessionIdParam);
      }
    }
  }, [location.state, location.search]);

  // Handle publishing the game
  const handlePublish = async () => {
    if (!previewCode) {
      setPublishError(
        "No game content to publish. Please generate a game first."
      );
      return;
    }

    const userId = getUserId();
    if (!userId) {
      setPublishError("You must be logged in to publish a game.");
      return;
    }

    if (!sessionId) {
      setPublishError("No active session found. Please save your chat first.");
      return;
    }

    try {
      setIsPublishing(true);
      setPublishError(null);

      // Try to call the API to publish the game
      try {
        // Add metadata to track that this is a remix if in remix mode
        const metadata = isRemixMode
          ? {
              isRemix: true,
              originalId:
                new URLSearchParams(location.search).get("originalGameId") ||
                "unknown",
            }
          : {};

        // Call the API to publish the game
        const result = await gameService.publishGame(
          sessionId,
          userId,
          previewCode,
          sessionTitle || "Untitled Game",
          metadata
        );

        // Set the shareable link and open the modal
        setShareableLink(result.shareableLink);
        setIsShareModalOpen(true);

        // Make sure we save this published version to localStorage too
        // so it appears in the user's projects
        const key = `chat_${userId}_${sessionId}`;
        let sessionData = {};
        try {
          const existing = localStorage.getItem(key);
          if (existing) {
            sessionData = JSON.parse(existing);
          }

          // Update with latest data
          sessionData.id = sessionId;
          sessionData.userId = userId;
          sessionData.title = sessionTitle || "Untitled Game";
          sessionData.publishedGameId = result.id || result.file_id;
          sessionData.publishedAt = new Date().toISOString();
          sessionData.updatedAt = new Date().toISOString();

          // Make sure we have at least one message to show in the UI
          if (!sessionData.messages || sessionData.messages.length === 0) {
            sessionData.messages = [
              {
                id: Date.now(),
                sender: "ai",
                text: "Your game has been published! You can share it using the link provided.",
              },
            ];
          }

          localStorage.setItem(key, JSON.stringify(sessionData));
        } catch (e) {
          console.error("Error updating local session data:", e);
        }

        console.log("Game published successfully:", result);
      } catch (apiError) {
        console.error("Failed to publish game:", apiError);

        // Fallback: Create a local storage entry and generate a local URL
        console.log("Using fallback local publishing mechanism");

        // Generate a unique ID for the game
        const localGameId = `local-game-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`;

        // Store the game content in localStorage
        localStorage.setItem(
          `game_${localGameId}`,
          JSON.stringify({
            id: localGameId,
            user_id: userId,
            session_id: sessionId,
            content: previewCode,
            title: sessionTitle || "Untitled Game",
            created_at: new Date().toISOString(),
            is_remix: isRemixMode,
          })
        );

        // Generate a shareable link using the local game ID
        const localShareableLink = `${window.location.origin}/play/${localGameId}`;

        // Set the shareable link and open the modal
        setShareableLink(localShareableLink);
        setIsShareModalOpen(true);

        console.log("Game published locally:", localGameId);
      }
    } catch (error) {
      console.error("Failed to publish game:", error);
      setPublishError("Failed to publish game. Please try again.");
    } finally {
      setIsPublishing(false);
    }
  };

  // Close the share modal
  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
  };

  return (
    <PageContainer>
      <Header>
        <LogoWrapper>
          <Logo
            src={isMobile ? mobileLogoImage : logoImage}
            alt="Logo"
            onClick={() => navigate("/")}
          />
        </LogoWrapper>
        {!isMobile ? (
          <ProjectName>
            {isEditingTitle ? (
              <TitleInput
                ref={titleInputRef}
                value={sessionTitle}
                onChange={(e) => setSessionTitle(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={handleTitleKeyPress}
                disabled={isSavingTitle}
                placeholder="Enter project title"
              />
            ) : (
              <TitleDisplay onClick={handleTitleEdit}>
                {sessionTitle || activeSession?.title || "Untitled Project"}
                <EditIcon>✎</EditIcon>
              </TitleDisplay>
            )}
            {isSavingTitle && <TitleSavingIndicator />}
          </ProjectName>
        ) : (
          <MobileProjectName>
            {isEditingTitle ? (
              <TitleInput
                ref={titleInputRef}
                value={sessionTitle}
                onChange={(e) => setSessionTitle(e.target.value)}
                onBlur={handleTitleSave}
                onKeyDown={handleTitleKeyPress}
                disabled={isSavingTitle}
                placeholder="Enter project title"
              />
            ) : (
              <TitleDisplay onClick={handleTitleEdit}>
                {sessionTitle || activeSession?.title || "Untitled Project"}
                <EditIcon>✎</EditIcon>
              </TitleDisplay>
            )}
            {isSavingTitle && <TitleSavingIndicator />}
          </MobileProjectName>
        )}
        <HeaderControls>
          {!isMobile ? (
            <>
              <UpgradeButton>
                <UpgradeButtonImg src={upgradeButtonImage} alt="Upgrade" />
              </UpgradeButton>

{/* --------------------------------------------------this is the publish button that u can remove starts from here ------------------------ */}

              <ActionButton
                onClick={handlePublish}
                disabled={isPublishing || !previewCode}
              >
                <ActionButtonImg src={publishButtonImage} alt="Publish" />
              </ActionButton>

{/* {/* --------------------------------------------------publish button end here   ------------------------------------ */}

            </>
          ) : (
            // Mobile view buttons with specified images
            <>
              <MobileHeaderButton
                onClick={() => {
                  console.log(
                    "Mobile Connect button clicked - Setting loginModal to open"
                  );
                  // Force isLoginModalOpen to true to ensure modal opens
                  setIsLoginModalOpen(true);
                  setTimeout(() => {
                    console.log(
                      "Login modal state after click:",
                      isLoginModalOpen
                    );
                  }, 100);
                }}
              >
                <MobileButtonImg src={mobileConnectImage} alt="Connect" />
              </MobileHeaderButton>
              <MobileHeaderButton>
                <MobileButtonImg src={mobileProjectImage} alt="Projects" />
              </MobileHeaderButton>
              <MobileHeaderButton
                onClick={handlePublish}
                disabled={isPublishing || !previewCode}
              >
                <MobileButtonImg src={mobilePublishImage} alt="Publish" />
              </MobileHeaderButton>
            </>
          )}
        </HeaderControls>
      </Header>

      <MainContainer>
        <ChatSection>
          <MemoryControls>
            {!isMobile ? (
              <>
                <MemoryButton
                  onClick={toggleHistory}
                  active={showHistory}
                  title="View chat history"
                >
                  <FontAwesomeIcon icon={faHistory} />
                  {loadingHistory ? "Loading..." : "History"}
                </MemoryButton>

                <MemoryButton
                  onClick={handleClearMemory}
                  title="Clear current chat memory"
                >
                  <FontAwesomeIcon icon={faEraser} />
                  Clear Memory
                </MemoryButton>

                <MemoryButton onClick={handleNewChat} title="Start a new chat">
                  <FontAwesomeIcon icon={faRedo} />
                  New Chat
                </MemoryButton>
              </>
            ) : (
              <>
                <MemoryButton
                  onClick={handleClearMemory}
                  title="Clear current chat memory"
                >
                  <FontAwesomeIcon icon={faEraser} />
                  Clear Memory
                </MemoryButton>

                <MemoryButton
                  onClick={toggleHistory}
                  active={showHistory}
                  title="View chat history"
                >
                  <FontAwesomeIcon icon={faHistory} />
                  History
                </MemoryButton>
              </>
            )}
          </MemoryControls>

          {showHistory && userSessions.length > 0 && (
            <SessionList>
              {userSessions.map((session) => (
                <SessionItem
                  key={session.id}
                  active={session.id === sessionId}
                  onClick={() => loadChatSession(session.id)}
                >
                  <SessionTitle>
                    {session.title || "Untitled Chat"}
                  </SessionTitle>
                  <SessionDate>
                    {new Date(
                      session.created_at || session.updatedAt
                    ).toLocaleDateString()}
                    <MessageCount>
                      {session.message_count || "0"} messages
                    </MessageCount>
                  </SessionDate>
                </SessionItem>
              ))}
            </SessionList>
          )}

          {!currentUser && (
            <GuestInfoPrompt>
              <GuestInfoIcon>👋</GuestInfoIcon>
              <GuestInfoText>
                You're chatting as a guest. Your sessions are saved on this
                device.
              </GuestInfoText>
              <ContinueButton onClick={() => setShowHistory(false)}>
                Continue as guest
              </ContinueButton>
            </GuestInfoPrompt>
          )}

          <ChatMessages ref={chatContainerRef}>
            {messages.map((msg) => (
              <MessageBubbleComponent
                key={msg.id}
                sender={msg.sender}
                isLoading={msg.isLoading}
                data-testid={`message-${msg.id}`}
              >
                {formatMessageText(msg.text, msg.isLoading)}
              </MessageBubbleComponent>
            ))}
            {error && <ErrorMessage>{error}</ErrorMessage>}
            <div ref={messagesEndRef} />
          </ChatMessages>

          <MessageForm onSubmit={handleSendMessage}>
            <MessageInputWrapper>
              <MessageInput
                type="text"
                placeholder={
                  isMobile
                    ? "Message Total AI"
                    : currentUser
                    ? `Message as ${getUserDisplayName()}`
                    : "Enter your message as guest"
                }
                value={message}
                onChange={handleMessageChange}
                disabled={loading}
              />
            </MessageInputWrapper>
            <SendButton type="submit" disabled={loading || !message.trim()}>
              <UpArrowIcon>↑</UpArrowIcon>
            </SendButton>
          </MessageForm>
        </ChatSection>

        {renderTabContent()}
      </MainContainer>

      {/* Only show ProfileMenu if user is logged in and menu is open */}
      {currentUser && isProfileMenuOpen && (
        <ProfileMenu isOpen={isProfileMenuOpen} onClose={closeProfileMenu} />
      )}
      {/* Login Modal - show for guests */}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
      {/* Share Modal for published games */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        shareableLink={shareableLink}
        gameTitle={sessionTitle || "Untitled Game"}
      />
      {/* Display publish error if any */}
      {publishError && (
        <ErrorNotification>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorText>{publishError}</ErrorText>
          <CloseErrorButton onClick={() => setPublishError(null)}>
            ×
          </CloseErrorButton>
        </ErrorNotification>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  font-family: "Space Grotesk", sans-serif;
  max-width: 100%;
  min-height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #ebebeb;
  background-color: white;

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #ebebeb;
    background-color: white;
    position: sticky;
    top: 0;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: flex-start;
    flex: 1;
  }
`;

const Logo = styled.img`
  height: 24px;

  @media (max-width: 768px) {
    height: 28px;
  }
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    display: flex;
    gap: 16px;
    position: relative;
    z-index: 25;
  }
`;

const GamePreview = styled.div`
  width: 100%;
  height: 100%;
  background: #594a5c;
  border-radius: 5px;
  overflow: hidden;
  position: relative;
`;

const GameGrid = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  background-image: linear-gradient(
      rgba(179, 186, 217, 0.2) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(179, 186, 217, 0.2) 1px, transparent 1px);
  background-size: 50px 50px;
  perspective: 500px;
  transform: rotateX(60deg);
  transform-style: preserve-3d;
`;

const GameObstacle = styled.div`
  position: absolute;
  width: 40px;
  height: 40px;
  background: black;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

// Create a custom MessageBubble component that can handle the isLoading prop
const MessageBubbleComponent = ({ children, sender, isLoading, ...rest }) => {
  return (
    <MessageBubbleStyled $sender={sender} $isLoading={isLoading} {...rest}>
      {children}
    </MessageBubbleStyled>
  );
};

// Rename the styled component to MessageBubbleStyled
const MessageBubbleStyled = styled.div`
  margin: 0.5rem 0;
  padding: 1rem 1.25rem;
  border-radius: 18px;
  max-width: 85%;
  word-wrap: break-word;
  line-height: 1.5;
  font-size: 0.95rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  position: relative;
  animation: ${(props) =>
    props.$isLoading
      ? "pulse 1.5s infinite ease-in-out"
      : "fadeIn 0.3s ease-in"};

  ${(props) =>
    props.$sender === "user"
      ? `
    background: #f0f0f0;
    color: #000;
    margin-left: auto;
    border: 1px solid #e0e0e0;
    border-bottom-right-radius: 4px;
    
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      right: -8px;
      width: 20px;
      height: 20px;
      background: linear-gradient(135deg, #DB0E9B, #DB0E9B);
      border-bottom-left-radius: 16px;
      clip-path: polygon(0 0, 0% 100%, 100% 100%);
      z-index: -1;
    }
  `
      : `
    background-color: ${props.$isLoading ? "#f5f7f9" : "white"};
    color: #333;
    border-bottom-left-radius: 4px;
    margin-right: auto;
    border: 1px solid #e1e4e8;
    
    &::before {
      content: '';
      position: absolute;
      bottom: 0;
      left: -8px;
      width: 20px;
      height: 20px;
      background: white;
      border-bottom-right-radius: 16px;
      border: 1px solid #e1e4e8;
      clip-path: polygon(0 100%, 100% 100%, 100% 0);
      z-index: -1;
    }
  `}

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes pulse {
    0% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0.6;
    }
  }

  .code-block-wrapper {
    margin: 1rem 0;
    border-radius: 8px;
    overflow: hidden;
  }

  .code-language {
    background-color: #2d2d2d;
    color: #ccc;
    font-size: 0.75rem;
    padding: 0.35rem 1rem;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  pre.code-block {
    background-color: #1e1e1e;
    color: #f8f8f2;
    padding: 1rem;
    margin: 0;
    overflow-x: auto;
    font-family: "Space Grotesk", "Consolas", "Monaco", monospace;
    font-size: 0.85rem;
  }

  code {
    font-family: "Space Grotesk", "Fira Code", monospace;
    font-size: 14px;
    color: #e6e6e6;
  }

  ${(props) =>
    props.$sender === "user" &&
    `
    .code-block-wrapper {
      background-color: rgba(0,0,0,0.1);
    }
    
    pre.code-block {
      background-color: rgba(0,0,0,0.2);
      color: white;
    }
    
    code {
      background-color: rgba(255,255,255,0.15);
      color: white;
    }
  `}
`;

const ErrorMessage = styled.div`
  padding: 1rem;
  color: red;
  text-align: center;
`;

const StreamingStatus = styled.div`
  display: flex;
  align-items: center;
  padding: 0.4rem 0.8rem;
  background: ${(props) => {
    switch (props.status) {
      case "connecting":
        return "#f8f9fa";
      case "streaming":
        return "#e6f7ff";
      case "stalled":
        return "#fff7e6";
      case "complete":
        return "#f6ffed";
      case "error":
        return "#fff1f0";
      default:
        return "#f8f9fa";
    }
  }};
  border: 1px solid
    ${(props) => {
      switch (props.status) {
        case "connecting":
          return "#dee2e6";
        case "streaming":
          return "#91d5ff";
        case "stalled":
          return "#ffe58f";
        case "complete":
          return "#b7eb8f";
        case "error":
          return "#ffccc7";
        default:
          return "#dee2e6";
      }
    }};
  border-radius: 999px;
  font-size: 0.75rem;
  color: ${(props) => {
    switch (props.status) {
      case "connecting":
        return "#495057";
      case "streaming":
        return "#0050b3";
      case "stalled":
        return "#ad6800";
      case "complete":
        return "#135200";
      case "error":
        return "#a8071a";
      default:
        return "#495057";
    }
  }};
  margin-left: auto;
`;

const MemoryButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: white;
  color: #666;
  border: 1px solid #ebebeb;
  border-radius: 999px;
  font-size: 0.85rem;
  cursor: pointer;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    padding: 0.35rem 0.75rem;
  }
`;

const NewChatButton = styled(MemoryButton)`
  background: linear-gradient(90deg, #0782fe 0%, #e80694 100%);
  color: white;
  border: none;

  &:hover {
    opacity: 0.9;
  }
`;

const SessionList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  padding: 0.5rem;
  border-bottom: 1px solid #eaeaea;
`;

const SessionItem = styled.div`
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  cursor: pointer;
  background: ${(props) => (props.active ? "rgba(0, 123, 255, 0.1)" : "white")};
  border: 1px solid ${(props) => (props.active ? "#007bff" : "#eaeaea")};

  &:hover {
    background: rgba(0, 123, 255, 0.05);
  }
`;

const SessionTitle = styled.div`
  font-weight: 500;
  font-size: 0.9rem;
  color: #333;
`;

const SessionDate = styled.div`
  font-size: 0.8rem;
  color: #999;
  margin-top: 0.25rem;
`;

const LoadingSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-left: 0.5rem;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const GuestInfoPrompt = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  margin: 2rem auto;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  border: 1px dashed #ddd;
`;

const GuestInfoIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const GuestInfoText = styled.p`
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ContinueButton = styled.button`
  background: #4f6fee;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.2s ease;

  &:hover {
    background: #3d5cdf;
  }
`;

const UserSessionHeader = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.75rem 1rem;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`;

const SessionLabel = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 0.25rem;
`;

const SessionId = styled.div`
  font-size: 0.9rem;
  color: #666;
  display: flex;
  justify-content: space-between;
`;

const MessageCount = styled.span`
  color: #999;
`;

// Now create a helper component to render messages with code blocks
const formatMessageText = (text, isLoading = false) => {
  if (!text) return <div>Please Wait...</div>;

  // If we're loading, show typing indicator for AI messages
  if (isLoading) {
    return (
      <TypingIndicator>
        <TypingDot delay="0s" />
        <TypingDot delay="0.2s" />
        <TypingDot delay="0.4s" />
      </TypingIndicator>
    );
  }

  // Make sure text is a string
  if (typeof text !== "string") {
    text = String(text);
  }

  // CRITICAL CHANGE: Modified regex to handle incomplete code blocks during streaming
  // This regex will detect code blocks even if they're not yet complete
  const parts = [];
  let lastIndex = 0;

  // First, try to find complete code blocks
  const completeCodeBlockRegex = /```([\w]*)\n([\s\S]*?)```/g;
  let match;

  while ((match = completeCodeBlockRegex.exec(text)) !== null) {
    // Add any text before this code block
    if (match.index > lastIndex) {
      parts.push({
        type: "text",
        content: text.substring(lastIndex, match.index),
      });
    }

    // Add the complete code block
    parts.push({
      type: "code",
      language: match[1] || "",
      content: match[2],
      complete: true,
    });

    lastIndex = match.index + match[0].length;
  }

  // Now check if there's an incomplete code block at the end
  if (lastIndex < text.length) {
    const remainingText = text.substring(lastIndex);
    const incompleteMatch = remainingText.match(/```([\w]*)\n?([\s\S]*)/);

    if (incompleteMatch && incompleteMatch.index === 0) {
      // We have an incomplete code block at the end
      parts.push({
        type: "code",
        language: incompleteMatch[1] || "",
        content: incompleteMatch[2] || "",
        complete: false,
      });
    } else {
      // Just regular text remaining
      parts.push({
        type: "text",
        content: remainingText,
      });
    }
  }

  return (
    <MessageContent>
      {parts.map((part, index) => {
        if (part.type === "code") {
          return (
            <CodeBlockWrapper key={index}>
              <CodeLanguageHeader>
                {part.language ? part.language.toUpperCase() : "CODE"}
              </CodeLanguageHeader>
              <CodeBlock>
                <div
                  dangerouslySetInnerHTML={{
                    __html: formatCode(part.content.trim(), part.language),
                  }}
                />
                {!part.complete && <span className="cursor">▋</span>}
              </CodeBlock>
            </CodeBlockWrapper>
          );
        } else {
          // Regular text - replace line breaks with <br>
          return (
            <span key={index}>
              {part.content.split("\n").map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i < part.content.split("\n").length - 1 && <br />}
                </React.Fragment>
              ))}
            </span>
          );
        }
      })}
    </MessageContent>
  );
};

// Function to apply basic syntax highlighting
const formatCode = (code, language) => {
  if (!code) return "";

  // Sanitize the code to prevent HTML injection
  const sanitized = code
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  if (language === "html") {
    // Highlight HTML
    return sanitized
      .replace(/(&lt;[\/]?[a-zA-Z0-9-]+)/g, '<span class="tag">$1</span>')
      .replace(
        /([a-zA-Z-]+)=(&quot;|&apos;)([^&quot;&apos;]*)(&quot;|&apos;)/g,
        '<span class="attr">$1</span>=<span class="string">$2$3$4</span>'
      );
  } else if (language === "css") {
    // Highlight CSS
    return sanitized
      .replace(
        /([a-zA-Z-#.][a-zA-Z0-9-_#.]*\s*\{)/g,
        '<span class="selector">$1</span>'
      )
      .replace(/([a-zA-Z-]+):/g, '<span class="property">$1</span>:')
      .replace(/(#[a-fA-F0-9]{3,6})/g, '<span class="number">$1</span>');
  } else if (language === "javascript" || language === "js") {
    // Highlight JavaScript
    return sanitized
      .replace(
        /\b(const|let|var|function|return|if|else|for|while|class|import|export|from|async|await)\b/g,
        '<span class="keyword">$1</span>'
      )
      .replace(
        /\b([a-zA-Z_$][a-zA-Z0-9_$]*)(?=\s*\()/g,
        '<span class="function">$1</span>'
      )
      .replace(/\b([0-9]+(\.[0-9]+)?)\b/g, '<span class="number">$1</span>')
      .replace(
        /(&quot;[^&quot;]*&quot;|&apos;[^&apos;]*&apos;|`[^`]*`)/g,
        '<span class="string">$1</span>'
      )
      .replace(/\/\/(.*?)$/gm, '<span class="comment">// $1</span>');
  }

  // For unknown languages or mixed code, just return sanitized code
  return sanitized;
};

// Escape HTML for code blocks to avoid rendering issues
const escapeHtml = (unsafe) => {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
};

// Styled components that are used in renderTabContent function
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  text-align: center;
  padding: 24px;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: #5f6368;
  max-width: 240px;
  line-height: 1.4;
`;

const EmptyStateIcon = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
  opacity: 0.7;
`;

// Enhance HTMLPreview component to make it fully interactive
const HTMLPreview = ({ code }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

      // Add necessary scripts and style resets
      const baseCSS = `
        html, body { 
          margin: 0; 
          padding: 0; 
          height: 100%; 
          overflow: auto; 
        }
      `;

      // Create a full HTML document with proper head and body
      const fullHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>${baseCSS}</style>
            <base target="_blank">
          </head>
          <body>
            ${code}
            <script>
              // Intercept console.log and errors to report back to parent frame
              window.onerror = function(message, source, lineno, colno, error) {
                window.parent.postMessage({ type: 'error', message, source, lineno }, '*');
                return true;
              };
              
              console.log = (function(originalLog) {
                return function(...args) {
                  window.parent.postMessage({ type: 'log', args: JSON.stringify(args) }, '*');
                  originalLog.apply(console, args);
                };
              })(console.log);
              
              // Add event listeners to window to allow interaction
              document.addEventListener('click', function(e) {
                if (e.target.tagName === 'BUTTON' || e.target.tagName === 'A') {
                  e.stopPropagation();
                }
              });
              
              // Force links to open in new tab
              document.querySelectorAll('a').forEach(a => {
                a.setAttribute('target', '_blank');
              });
            </script>
          </body>
        </html>
      `;

      // Apply the HTML to the iframe
      iframeDoc.open();

      // If code already includes <!DOCTYPE html> or <html>, use it directly
      if (code.includes("<!DOCTYPE html>") || code.includes("<html")) {
        iframeDoc.write(code);
      } else {
        iframeDoc.write(fullHTML);
      }

      iframeDoc.close();

      // Set up message listener for console logs and errors
      const handleMessage = (event) => {
        if (event.data.type === "log") {
          console.log("Preview console:", ...JSON.parse(event.data.args));
        } else if (event.data.type === "error") {
          console.error(
            "Preview error:",
            event.data.message,
            "at line",
            event.data.lineno
          );
        }
      };

      window.addEventListener("message", handleMessage);

      return () => {
        window.removeEventListener("message", handleMessage);
      };
    }
  }, [code]);

  return (
    <StyledIframe
      ref={iframeRef}
      title="Interactive Preview"
      sandbox="allow-scripts allow-same-origin allow-forms"
    />
  );
};

// Update the StyledIframe component to be more interactive
const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

// Add the CodeDisplayContainer BEFORE CodePreviewContainer
const CodeDisplayContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow: auto;
  padding: 1rem;

  pre {
    font-family: "Space Grotesk", "Consolas", "Monaco", "Courier New", monospace;
    color: #f8f8f2;
    font-size: 0.9rem;
    line-height: 1.5;
    white-space: pre-wrap;
    background-color: #1e1e1e;
    padding: 1rem;
    border-radius: 6px;
  }
`;

// Update CodePreviewContainer for better UI
const CodePreviewContainer = styled.div`
  width: 100%;
  height: 100%;
  background: white;
  border-radius: 5px;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const EmptyPreview = styled(EmptyState)`
  background: #f8f9fa;
  height: 100%;
`;

const CodeDisplay = styled.div`
  flex: 1;
  background: #282c34;
  padding: 16px;
  overflow: auto;
  font-family: "Space Grotesk", "Consolas", "Monaco", monospace;

  pre {
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }

  code {
    font-family: "Space Grotesk", "Fira Code", monospace;
    font-size: 14px;
    color: #e6e6e6;
  }
`;

const CodeSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e0e0e0;
  overflow: hidden;

  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
    min-height: 300px;
  }
`;

const AssetsSection = styled.div`
  flex: 1;
  padding: 16px;
  overflow: auto;
  background: #f8f9fa;
  border-radius: 12px;
  margin-left: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Spacer = styled.div`
  flex: 1;
`;

const ProjectInput = styled.input`
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  font-size: 0.9rem;
  width: 100%;
  max-width: 250px;

  @media (max-width: 768px) {
    background-color: white;
    margin: 0 auto;
    display: block;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    box-shadow: none;
    border: 1px solid #eaeaea;
  }
`;

const MobileHeaderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  z-index: 20;
  position: relative;

  &:active {
    opacity: 0.7;
  }
`;

const MobileButtonImg = styled.img`
  height: 32px;
  width: auto;
`;

const SplitViewContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SectionHeader = styled.div`
  padding: 8px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  font-weight: 500;
  font-size: 14px;
  color: #333;
`;

// UI Components - Add these below existing styled components
const PreviewSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 16px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: linear-gradient(
    135deg,
    rgba(7, 130, 254, 0.1),
    rgba(219, 14, 155, 0.1)
  );
  color: linear-gradient(135deg, #e80694, #0782fe);

  @media (max-width: 768px) {
    margin: 8px;
    min-height: 40vh;
    flex: none;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  background: #ffeef7;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  padding: 0;

  @media (max-width: 768px) {
    background: #f5f5f5;
  }
`;

const Tab = styled.div`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 500;
  color: ${(props) => (props.active ? "#6366F1" : "#666")};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
  position: relative;
  flex: 1;
  justify-content: center;
  border-bottom: ${(props) => (props.active ? "2px solid #6366F1" : "none")};
  background: ${(props) => (props.active ? "white" : "#ffeef7")};

  &:hover {
    color: #6366f1;
  }
`;

const TabIcon = styled.span`
  font-size: 16px;
  color: ${(props) => (props.active ? "#6366F1" : "#666")};
`;

const TabContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
  background: white;

  @media (max-width: 768px) {
    flex: 1;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const EmptyPreviewContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;

  /* 3D grid effect */
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 70%;
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.1) 1px,
        transparent 1px
      ),
      linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
    background-size: 50px 50px;
    perspective: 500px;
    transform: rotateX(60deg);
    transform-style: preserve-3d;
    opacity: 0.7;
    z-index: 1;
  }
`;

const PreviewContainer = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  background: #fff;
  overflow: hidden;

  @media (max-width: 768px) {
    min-height: 300px;
  }
`;

const GradientBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #e80694 0%, #0782fe 100%);
  opacity: 0.2;
  z-index: 0;
`;

const PreviewControls = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 10;
`;

const RefreshButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: #666;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f8f8f8;
  }
`;

const ExpandButton = styled.button`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: #666;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f8f8f8;
  }
`;

const UserAvatar = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(90deg, #0782fe 0%, #e80694 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
`;

const UserName = styled.div`
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
`;

const UpgradeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const UpgradeButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const ConnectButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const ConnectButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  color: #333;
  border: none;
  border-radius: 5px;
  font-weight: 500;
  cursor: pointer;
`;

const ActionButtonImg = styled.img`
  height: 36px;
  width: auto;
`;

const MainContainer = styled.div`
  display: flex;
  height: calc(100vh - 60px);

  @media (max-width: 768px) {
    flex-direction: column;
    height: calc(100vh - 60px);
    overflow: auto;
    position: relative;
    z-index: 5;
  }
`;

const ChatSection = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #ebebeb;

  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    flex: none;
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: 50vh;
  }
`;

const MemoryControls = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    justify-content: center;
    gap: 0.5rem;
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  scroll-behavior: smooth;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    max-height: calc(50vh - 120px); /* Account for input field and buttons */
  }
`;

const MessageForm = styled.form`
  display: flex;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #eaeaea;
  gap: 10px;

  @media (max-width: 768px) {
    padding: 0.75rem;
    background: white;
    z-index: 10;
    margin-top: auto;
  }
`;

const MessageInputWrapper = styled.div`
  position: relative;
  flex: 1;

  &::before {
    content: "";
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(90deg, #0782fe, #db0e9b);
    border-radius: 22px;
    z-index: 0;
  }

  @media (max-width: 768px) {
    &::before {
      border-radius: 10px;
      top: -1px;
      left: -1px;
      right: -1px;
      bottom: -1px;
      background: transparent;
    }
  }
`;

const MessageInput = styled.input`
  width: 100%;
  padding: 0.85rem 1rem;
  border: 1px solid transparent;
  border-radius: 20px;
  outline: none;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
  background-color: white;
  position: relative;
  z-index: 1;

  &:focus {
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    font-size: 0.9rem;
    padding: 0.75rem 1rem;
    background-color: #f5f5f5;
    border-radius: 8px;
  }
`;

const SendButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #000000;
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background-color: #333333;
    transform: scale(1.05);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const UpArrowIcon = styled.span`
  font-weight: bold;
`;

const ProjectName = styled.div`
  position: relative;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: center;
  margin: 0 15px;
  max-width: 400px;
`;

const MobileProjectName = styled.div`
  position: relative;
  font-size: 16px;
  font-weight: 600;
  color: #333;
  display: flex;
  align-items: center;
  flex: 1;
  margin: 0 10px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TitleDisplay = styled.div`
  display: flex;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  border: 1px solid #e0e0e0;
  position: relative;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    border-color: #bbbbbb;
  }
`;

const EditIcon = styled.span`
  opacity: 1;
  margin-left: 8px;
  font-size: 14px;
  color: #666;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  background-color: #f5f5f5;

  &:hover {
    background-color: #e9e9e9;
    color: #333;
  }
`;

const TitleInput = styled.input`
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 4px 8px;
  width: 100%;
  outline: none;

  &:focus {
    border-color: #4f6fee;
    box-shadow: 0 0 0 2px rgba(79, 111, 238, 0.3);
  }
`;

const TitleSavingIndicator = styled.div`
  position: absolute;
  right: -20px;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(79, 111, 238, 0.3);
  border-top: 2px solid #4f6fee;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: 1rem;
  padding: 0.5rem;
  border-radius: 25px;
  background: rgba(0, 0, 0, 0.05);
`;

const GuestLabel = styled.span`
  font-size: 12px;
  color: #666;
  background-color: #f0f0f0;
  padding: 4px 8px;
  border-radius: 12px;
  margin-left: 8px;
`;

// Add the styled component for ProfileImage
const ProfileImage = styled.img.attrs({
  className: "sc-cxgKzJ WpOOx",
})`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  margin-right: 8px;
`;

const ErrorNotification = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #ffebee;
  color: #c62828;
  border-left: 4px solid #c62828;
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  max-width: 400px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  animation: ${fadeIn} 0.3s ease-out;
`;

const ErrorIcon = styled.span`
  font-size: 18px;
  margin-right: 10px;
`;

const ErrorText = styled.div`
  flex: 1;
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
`;

const CloseErrorButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #c62828;
  padding: 0;
  margin-left: 10px;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`;

const AssetsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(249, 250, 251, 0.8);
  border-radius: 8px;
  padding: 2rem;
`;

const TortoiseImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${dropIn} 1s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
`;

const TortoiseImage = styled.img`
  width: 270px;
  height: auto;
  border-radius: 10px;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0px 10px 15px rgba(0, 0, 0, 0.1));
  animation: ${bounce} 2s ease-in-out infinite;
  animation-delay: 1s;
`;

const ComingSoonText = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: #6b46c1;
  text-align: center;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.3));
  border-radius: 50px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(5px);
  margin-top: 1rem;
`;

export default ChatPage;
